import React, { useState, useEffect, useCallback } from "react";
import {
  MapPin,
  Star,
  MessageSquare,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  Plus,
  RefreshCw,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Eye,
  Phone,
  Globe,
  Navigation,
  BarChart3,
  Calendar,
  Mail,
  Edit3,
  Trash2,
  HelpCircle,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  Copy,
  X,
  Search,
} from "lucide-react";
import api from "../api/client";

const TABS = [
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "posts", label: "Posts", icon: Send },
  { key: "insights", label: "Insights", icon: BarChart3 },
  { key: "qa", label: "Q&A", icon: HelpCircle },
];

const POST_STATUS = {
  draft: { label: "Draft", color: "text-gray-400", bg: "bg-gray-800/40" },
  scheduled: { label: "Scheduled", color: "text-blue-400", bg: "bg-blue-900/20" },
  published: { label: "Published", color: "text-green-400", bg: "bg-green-900/20" },
  failed: { label: "Failed", color: "text-red-400", bg: "bg-red-900/20" },
};

const SENTIMENT_MAP = {
  positive: { icon: ThumbsUp, color: "text-green-400", bg: "bg-green-900/20" },
  neutral: { icon: Minus, color: "text-gray-400", bg: "bg-gray-800/40" },
  negative: { icon: ThumbsDown, color: "text-red-400", bg: "bg-red-900/20" },
};

function StarRating({ rating, size = 14 }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "text-amber-400 fill-amber-400" : "text-gray-600"}
        />
      ))}
    </span>
  );
}

// ─── Connection Card ───
function ConnectionCard({ siteId, gbp, onRefresh }) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const [placeId, setPlaceId] = useState(gbp?.place_id || "");
  const [savingPlaceId, setSavingPlaceId] = useState(false);
  const [placeIdSaved, setPlaceIdSaved] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  useEffect(() => { setPlaceId(gbp?.place_id || ""); setValidation(null); }, [gbp?.place_id]);

  const handleSavePlaceId = async () => {
    setSavingPlaceId(true);
    setPlaceIdSaved(false);
    try {
      await api.post(`/websites/${siteId}/gbp/place-id/`, { place_id: placeId });
      setPlaceIdSaved(true);
      setValidation(null);
      setTimeout(() => setPlaceIdSaved(false), 3000);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save Place ID");
    }
    setSavingPlaceId(false);
  };

  const handleSearchPlaceId = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await api.post(`/websites/${siteId}/gbp/search-place-id/`, { query: searchQuery });
      setSearchResults(res.data.results || []);
    } catch (err) {
      setError(err.response?.data?.error || "Search failed");
    }
    setSearching(false);
  };

  const handleValidatePlaceId = async () => {
    if (!placeId.trim()) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await api.post(`/websites/${siteId}/gbp/validate-place-id/`, { place_id: placeId });
      setValidation(res.data);
    } catch (err) {
      setValidation({ valid: false, error: err.response?.data?.error || "Validation failed" });
    }
    setValidating(false);
  };

  const handleSelectResult = (result) => {
    setPlaceId(result.place_id);
    setSearchResults(null);
    setSearchQuery("");
    setValidation({
      valid: true,
      has_reviews: !!result.review_count,
      name: result.name,
      address: result.address,
      rating: result.rating,
      review_count: result.review_count,
    });
  };

  const handleOAuthConnect = async () => {
    setConnecting(true);
    setError("");
    try {
      const res = await api.post(`/websites/${siteId}/gbp/oauth/connect/`);
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        setError("Failed to get authorization URL");
        setConnecting(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start OAuth flow");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Business Profile?")) return;
    await api.post(`/websites/${siteId}/gbp/disconnect/`);
    onRefresh();
  };

  if (gbp?.is_connected) {
    return (
      <div className="bg-dark-200 rounded-xl border border-dark-50 p-5 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-900/20 flex items-center justify-center">
              <MapPin size={24} className="text-green-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{gbp.location_name || "Connected"}</h3>
              <p className="text-gray-500 text-sm">{gbp.address || gbp.category || "Google Business Profile"}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-400">{gbp.average_rating?.toFixed(1) || "0.0"}</div>
              <StarRating rating={Math.round(gbp.average_rating || 0)} size={12} />
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{gbp.total_reviews || 0}</div>
              <div className="text-xs text-gray-500">Reviews</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-400 bg-green-900/20 px-2 py-1 rounded-full">Connected</span>
              <button
                onClick={handleDisconnect}
                className="text-xs text-gray-500 hover:text-red-400 transition-colors"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
        {gbp.needs_reauth && (
          <div className="mt-3 bg-amber-900/20 border border-amber-800/30 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-400" />
              <span className="text-sm text-amber-300">
                Google authorization expired. Reconnect to sync reviews and insights.
              </span>
            </div>
            <button
              onClick={handleOAuthConnect}
              disabled={connecting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {connecting ? <Loader size={12} className="animate-spin" /> : <ArrowRight size={12} />}
              Reconnect
            </button>
          </div>
        )}
        {/* Place ID for Places API fallback */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 whitespace-nowrap">Place ID:</label>
            <input
              type="text"
              value={placeId}
              onChange={(e) => { setPlaceId(e.target.value); setValidation(null); }}
              placeholder="ChIJ... (Google Maps Place ID)"
              className="flex-1 bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:border-copper focus:outline-none"
            />
            <button
              onClick={handleValidatePlaceId}
              disabled={validating || !placeId.trim()}
              className="px-3 py-1.5 bg-dark-50 text-gray-300 text-xs font-medium rounded-lg hover:bg-dark-50/80 transition-colors disabled:opacity-40"
              title="Validate this Place ID"
            >
              {validating ? <Loader size={12} className="animate-spin" /> : "Validate"}
            </button>
            <button
              onClick={handleSavePlaceId}
              disabled={savingPlaceId || placeId === (gbp.place_id || "")}
              className="px-3 py-1.5 bg-copper/20 text-copper text-xs font-medium rounded-lg hover:bg-copper/30 transition-colors disabled:opacity-40"
            >
              {savingPlaceId ? "Saving..." : placeIdSaved ? "Saved!" : "Save"}
            </button>
          </div>

          {/* Validation result */}
          {validation && (
            <div className={`text-xs rounded-lg px-3 py-2 ml-[52px] ${
              validation.warning || validation.is_address
                ? "bg-amber-900/20 border border-amber-800/30 text-amber-300"
                : validation.has_reviews
                  ? "bg-green-900/20 border border-green-800/30 text-green-300"
                  : "bg-gray-800 border border-gray-700 text-gray-300"
            }`}>
              {validation.warning && <div className="mb-1">{validation.warning}</div>}
              {validation.name && <span className="font-medium">{validation.name}</span>}
              {validation.address && <span className="text-gray-400"> — {validation.address}</span>}
              {validation.rating != null && (
                <span className="ml-2 text-amber-400">
                  {validation.rating} ({validation.review_count || 0} reviews)
                </span>
              )}
              {!validation.valid && validation.error && (
                <span className="text-red-400">{validation.error}</span>
              )}
            </div>
          )}

          {/* Search for Place ID */}
          <div className="flex items-center gap-2 ml-[52px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchPlaceId()}
              placeholder="Search business name to find Place ID..."
              className="flex-1 bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-600 focus:border-copper focus:outline-none"
            />
            <button
              onClick={handleSearchPlaceId}
              disabled={searching || !searchQuery.trim()}
              className="flex items-center gap-1 px-3 py-1.5 bg-dark-50 text-gray-300 text-xs font-medium rounded-lg hover:bg-dark-50/80 transition-colors disabled:opacity-40"
            >
              {searching ? <Loader size={12} className="animate-spin" /> : <Search size={12} />}
              Search
            </button>
          </div>

          {/* Search results */}
          {searchResults && (
            <div className="ml-[52px] space-y-1">
              {searchResults.length === 0 ? (
                <div className="text-xs text-gray-500 py-2">No businesses found. Try a different search.</div>
              ) : (
                searchResults.map((r) => (
                  <div
                    key={r.place_id}
                    onClick={() => handleSelectResult(r)}
                    className="flex items-center justify-between bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 cursor-pointer hover:border-copper/40 transition-colors"
                  >
                    <div>
                      <span className="text-xs font-medium text-white">{r.name}</span>
                      <span className="text-[10px] text-gray-500 ml-2">{r.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      {r.rating && <span className="text-amber-400">{r.rating}</span>}
                      {r.review_count != null && <span className="text-gray-400">({r.review_count} reviews)</span>}
                      <span className="text-copper font-mono">{r.place_id.substring(0, 12)}...</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          <p className="text-[11px] text-gray-600 ml-[52px]">
            Required for syncing reviews via Google Places API. Search above or find on Google Maps.
          </p>
        </div>
        {gbp.last_synced && (
          <div className="text-xs text-gray-600 mt-3">
            Last synced: {new Date(gbp.last_synced).toLocaleString()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-dark-200 rounded-xl border border-dark-50 p-8 mb-6 text-center">
      <MapPin size={40} className="text-gray-600 mx-auto mb-3" />
      <h3 className="text-white font-semibold mb-1">Connect Google Business Profile</h3>
      <p className="text-gray-500 text-sm mb-6">
        Sign in with your Google account to automatically connect your business listing.
        This will allow you to manage reviews, posts, and insights.
      </p>
      {error && (
        <div className="max-w-md mx-auto mb-4 text-sm text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-2.5">
          {error}
        </div>
      )}
      <div className="max-w-md mx-auto">
        <button
          onClick={handleOAuthConnect}
          disabled={connecting}
          className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-lg px-4 py-3 text-sm transition-colors disabled:opacity-40"
        >
          {connecting ? (
            <Loader size={18} className="animate-spin" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          {connecting ? "Connecting..." : "Connect with Google"}
        </button>
      </div>
    </div>
  );
}

// ─── Reviews Tab ───
function ReviewsTab({ siteId }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedReview, setSelectedReview] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [responding, setResponding] = useState(false);
  const [requestModal, setRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ client_name: "", client_email: "" });
  const [requestSending, setRequestSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "unresponded") params.responded = "false";
      else if (filter === "positive") params.rating = "5";
      else if (filter === "negative") params.rating = "1";

      const [revRes, statsRes] = await Promise.all([
        api.get(`/websites/${siteId}/gbp/reviews/`, { params }),
        api.get(`/websites/${siteId}/gbp/reviews/stats/`),
      ]);
      setReviews(revRes.data.results || revRes.data);
      setStats(statsRes.data);
    } catch {
      // handled
    }
    setLoading(false);
  }, [siteId, filter]);

  useEffect(() => { load(); }, [load]);

  const [syncError, setSyncError] = useState("");

  const handleSync = async () => {
    setSyncing(true);
    setSyncError("");
    try {
      await api.post(`/websites/${siteId}/gbp/reviews/sync/`);
      setTimeout(load, 3000);
    } catch (err) {
      setSyncError(err.response?.data?.error || "Sync failed");
    }
    setSyncing(false);
  };

  const handleGenerateAI = async (review) => {
    setGenerating(true);
    try {
      const res = await api.post(`/websites/${siteId}/gbp/reviews/${review.id}/ai-response/`);
      setResponseText(res.data.suggestion || "");
    } catch {
      // handled
    }
    setGenerating(false);
  };

  const handleRespond = async () => {
    if (!responseText.trim() || !selectedReview) return;
    setResponding(true);
    try {
      await api.post(`/websites/${siteId}/gbp/reviews/${selectedReview.id}/respond/`, {
        response_text: responseText,
      });
      setSelectedReview(null);
      setResponseText("");
      load();
    } catch {
      // handled
    }
    setResponding(false);
  };

  const handleRequestReview = async () => {
    if (!requestForm.client_name || !requestForm.client_email) return;
    setRequestSending(true);
    try {
      await api.post(`/websites/${siteId}/gbp/reviews/request/`, requestForm);
      setRequestModal(false);
      setRequestForm({ client_name: "", client_email: "" });
    } catch {
      // handled
    }
    setRequestSending(false);
  };

  return (
    <div>
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: "Avg Rating", value: stats.average_rating?.toFixed(1) || "0.0", icon: Star, color: "text-amber-400" },
            { label: "Total Reviews", value: stats.total_reviews, icon: MessageSquare, color: "text-copper" },
            { label: "Response Rate", value: `${stats.response_rate || 0}%`, icon: CheckCircle, color: "text-green-400" },
            { label: "Unresponded", value: stats.unresponded || 0, icon: AlertCircle, color: stats.unresponded > 0 ? "text-red-400" : "text-gray-400" },
          ].map((s, i) => (
            <div key={i} className="bg-dark-200 rounded-xl border border-dark-50 p-4">
              <div className="flex items-center justify-between mb-2">
                <s.icon size={18} className={s.color} />
              </div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {[
            { key: "all", label: "All" },
            { key: "unresponded", label: "Unresponded" },
            { key: "positive", label: "5 Star" },
            { key: "negative", label: "1 Star" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.key
                  ? "bg-copper/10 text-copper border border-copper/30"
                  : "bg-dark-200 text-gray-400 border border-dark-50 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setRequestModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-dark-200 text-gray-400 border border-dark-50 hover:text-white transition-colors"
          >
            <Mail size={14} />
            Request Review
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-copper/10 text-copper border border-copper/30 hover:bg-copper/20 transition-colors"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing..." : "Sync Reviews"}
          </button>
        </div>
      </div>

      {syncError && (
        <div className="bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-300">{syncError}</span>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={24} className="animate-spin text-copper" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Star size={40} className="mx-auto mb-3 text-gray-600" />
          <p>No reviews yet. Sync from Google or request reviews from clients.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => {
            const sent = SENTIMENT_MAP[review.sentiment] || SENTIMENT_MAP.neutral;
            const SentIcon = sent.icon;
            return (
              <div
                key={review.id}
                className={`bg-dark-200 rounded-xl border border-dark-50 p-4 cursor-pointer hover:border-dark-50/80 transition-colors ${
                  selectedReview?.id === review.id ? "border-copper/40" : ""
                }`}
                onClick={() => {
                  setSelectedReview(review);
                  setResponseText(review.ai_suggested_response || "");
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {review.reviewer_photo_url ? (
                      <img
                        src={review.reviewer_photo_url}
                        alt=""
                        className="w-10 h-10 rounded-full bg-dark-100"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-dark-100 flex items-center justify-center text-gray-500 text-sm font-bold">
                        {review.reviewer_name?.[0] || "?"}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">{review.reviewer_name}</span>
                        <StarRating rating={review.rating} size={12} />
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sent.bg} ${sent.color}`}>
                          <SentIcon size={10} className="inline mr-0.5" />
                          {review.sentiment || "—"}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{review.review_text || "No text"}</p>
                      <div className="text-xs text-gray-600 mt-1.5">
                        {new Date(review.review_date).toLocaleDateString()}
                        {review.source !== "google" && (
                          <span className="ml-2 text-gray-600">via {review.source}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {review.responded ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-900/20 text-green-400">
                        Responded
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-900/20 text-amber-400">
                        Needs Response
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Response Panel */}
      {selectedReview && !selectedReview.responded && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-300 rounded-xl border border-dark-50 w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-dark-50">
              <h3 className="text-white font-semibold">Respond to Review</h3>
              <button onClick={() => setSelectedReview(null)} className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-white text-sm font-medium">{selectedReview.reviewer_name}</span>
                <StarRating rating={selectedReview.rating} size={12} />
              </div>
              <p className="text-gray-400 text-sm mb-4">{selectedReview.review_text}</p>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={5}
                placeholder="Write your response..."
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper resize-none"
              />
              <div className="flex justify-between mt-3">
                <button
                  onClick={() => handleGenerateAI(selectedReview)}
                  disabled={generating}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-dark-200 text-copper border border-copper/30 hover:bg-copper/10 transition-colors"
                >
                  <Sparkles size={14} className={generating ? "animate-pulse" : ""} />
                  {generating ? "Generating..." : "AI Suggest"}
                </button>
                <button
                  onClick={handleRespond}
                  disabled={responding || !responseText.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-copper text-dark hover:bg-copper-600 transition-colors disabled:opacity-40"
                >
                  <Send size={14} />
                  {responding ? "Posting..." : "Post Response"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Review Modal */}
      {requestModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-300 rounded-xl border border-dark-50 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-dark-50">
              <h3 className="text-white font-semibold">Request a Review</h3>
              <button onClick={() => setRequestModal(false)} className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <input
                type="text"
                placeholder="Client Name"
                value={requestForm.client_name}
                onChange={(e) => setRequestForm({ ...requestForm, client_name: e.target.value })}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
              <input
                type="email"
                placeholder="Client Email"
                value={requestForm.client_email}
                onChange={(e) => setRequestForm({ ...requestForm, client_email: e.target.value })}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
              <button
                onClick={handleRequestReview}
                disabled={requestSending || !requestForm.client_name || !requestForm.client_email}
                className="w-full bg-copper hover:bg-copper-600 text-dark font-medium rounded-lg px-4 py-2.5 text-sm transition-colors disabled:opacity-40"
              >
                {requestSending ? "Sending..." : "Send Review Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Posts Tab ───
function PostsTab({ siteId, selectedSite }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showAutoGen, setShowAutoGen] = useState(false);
  const [autoGenning, setAutoGenning] = useState(false);
  const [autoGenResult, setAutoGenResult] = useState(null);
  const [form, setForm] = useState({
    post_type: "update",
    title: "",
    content: "",
    cta_type: "learn_more",
    cta_url: "",
    scheduled_for: "",
  });
  const [autoForm, setAutoForm] = useState({
    service_slugs: "interior-painting, exterior-painting, kitchen-remodeling, bathroom-remodeling, cabinet-painting, pressure-washing",
    city_slugs: "yonkers, white-plains, new-rochelle, scarsdale, mount-vernon, bronxville, tarrytown, mamaroneck",
    posts_per_week: 2,
    post_types: "update, offer",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/websites/${siteId}/gbp/posts/`);
      setPosts(res.data.results || res.data);
    } catch {
      // handled
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/websites/${siteId}/gbp/posts/generate/`, {
        post_type: form.post_type,
      });
      setForm((f) => ({
        ...f,
        title: res.data.title || "",
        content: res.data.content || "",
        cta_url: res.data.cta_url || f.cta_url,
      }));
    } catch {
      // handled
    }
    setGenerating(false);
  };

  const handleCreate = async () => {
    if (!form.content.trim()) return;
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.scheduled_for) delete payload.scheduled_for;
      await api.post(`/websites/${siteId}/gbp/posts/`, payload);
      setShowCreate(false);
      setForm({ post_type: "update", title: "", content: "", cta_type: "learn_more", cta_url: "", scheduled_for: "" });
      load();
    } catch {
      // handled
    }
    setCreating(false);
  };

  const handlePublish = async (postId) => {
    try {
      await api.post(`/websites/${siteId}/gbp/posts/${postId}/publish/`);
      load();
    } catch {
      // handled
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/websites/${siteId}/gbp/posts/${postId}/`);
      load();
    } catch {
      // handled
    }
  };

  const handleAutoGenerate = async () => {
    const services = autoForm.service_slugs.split(",").map((s) => s.trim()).filter(Boolean);
    const cities = autoForm.city_slugs.split(",").map((s) => s.trim()).filter(Boolean);
    const postTypes = autoForm.post_types.split(",").map((s) => s.trim()).filter(Boolean);
    const slugToTitle = (slug) => slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

    if (!services.length || !cities.length) return;
    if (!window.confirm(`Generate ${services.length * cities.length} GBP posts for ${selectedSite?.domain || "this site"}?`)) return;

    setAutoGenning(true);
    setAutoGenResult(null);
    try {
      const res = await api.post(`/websites/${siteId}/gbp/posts/auto-generate/`, {
        service_slugs: services,
        city_slugs: cities,
        service_names: services.map(slugToTitle),
        city_names: cities.map(slugToTitle),
        posts_per_week: parseInt(autoForm.posts_per_week) || 2,
        post_types: postTypes,
        phone: selectedSite?.phone_number || "(347) 961-7357",
      });
      setAutoGenResult(res.data);
      load();
    } catch (err) {
      alert(err.response?.data?.error || "Auto-generation failed");
    }
    setAutoGenning(false);
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">GBP Posts</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowAutoGen(!showAutoGen); setShowCreate(false); }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-dark-200 text-gray-400 border border-dark-50 hover:text-white transition-colors"
          >
            <Calendar size={14} />
            Auto-Generate
          </button>
          <button
            onClick={() => { setShowCreate(!showCreate); setShowAutoGen(false); }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-copper/10 text-copper border border-copper/30 hover:bg-copper/20 transition-colors"
          >
            <Plus size={14} />
            New Post
          </button>
        </div>
      </div>

      {/* Auto-Generate Form */}
      {showAutoGen && (
        <div className="bg-dark-200 rounded-xl border border-copper/30 p-5 mb-6">
          <h4 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
            <Calendar size={16} className="text-copper" /> Auto-Generate Post Schedule
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            Generate a batch of GBP posts rotating through your services and cities.
            Posts are scheduled evenly and link to your city landing pages.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Service Slugs (comma-separated)</label>
              <textarea value={autoForm.service_slugs} onChange={(e) => setAutoForm({ ...autoForm, service_slugs: e.target.value })} rows={2}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-copper font-mono" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">City Slugs (comma-separated)</label>
              <textarea value={autoForm.city_slugs} onChange={(e) => setAutoForm({ ...autoForm, city_slugs: e.target.value })} rows={2}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-copper font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Posts Per Week</label>
                <input type="number" min={1} max={7} value={autoForm.posts_per_week}
                  onChange={(e) => setAutoForm({ ...autoForm, posts_per_week: e.target.value })}
                  className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-copper" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Post Types (rotate)</label>
                <input type="text" value={autoForm.post_types}
                  onChange={(e) => setAutoForm({ ...autoForm, post_types: e.target.value })}
                  className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-copper font-mono" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-gray-500">
                {autoForm.service_slugs.split(",").filter(Boolean).length * autoForm.city_slugs.split(",").filter(Boolean).length} posts will be created
              </span>
              <div className="flex gap-2">
                <button onClick={() => setShowAutoGen(false)}
                  className="px-3 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-white transition-colors">Cancel</button>
                <button onClick={handleAutoGenerate} disabled={autoGenning}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-copper text-dark hover:bg-copper/90 transition-colors disabled:opacity-40">
                  {autoGenning ? <Loader size={14} className="animate-spin" /> : <Calendar size={14} />}
                  {autoGenning ? "Generating..." : "Generate & Schedule"}
                </button>
              </div>
            </div>
          </div>
          {autoGenResult && (
            <div className="mt-4 bg-green-900/10 border border-green-800/30 rounded-lg p-3 text-sm text-green-400 flex items-center gap-2">
              <CheckCircle size={14} /> Created {autoGenResult.created} scheduled posts
            </div>
          )}
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <div className="bg-dark-200 rounded-xl border border-dark-50 p-5 mb-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Post Type</label>
              <select
                value={form.post_type}
                onChange={(e) => setForm({ ...form, post_type: e.target.value })}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-copper"
              >
                <option value="update">What's New</option>
                <option value="offer">Offer</option>
                <option value="event">Event</option>
                <option value="product">Product</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Post title..."
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs text-gray-500 mb-1 block">Content</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={4}
              placeholder="Post content (max 1,500 chars)..."
              maxLength={1500}
              className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper resize-none"
            />
            <div className="text-right text-xs text-gray-600 mt-1">{form.content.length}/1500</div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">CTA Type</label>
              <select
                value={form.cta_type}
                onChange={(e) => setForm({ ...form, cta_type: e.target.value })}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-copper"
              >
                <option value="">None</option>
                <option value="learn_more">Learn More</option>
                <option value="book">Book</option>
                <option value="order">Order Online</option>
                <option value="call">Call Now</option>
                <option value="sign_up">Sign Up</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">CTA URL</label>
              <input
                type="url"
                value={form.cta_url}
                onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                placeholder="https://..."
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Schedule For</label>
              <input
                type="datetime-local"
                value={form.scheduled_for}
                onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-copper"
              />
            </div>
          </div>
          <div className="flex justify-between">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-dark-100 text-copper border border-copper/30 hover:bg-copper/10 transition-colors"
            >
              <Sparkles size={14} className={generating ? "animate-pulse" : ""} />
              {generating ? "Generating..." : "AI Generate"}
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-3 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !form.content.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-copper text-dark hover:bg-copper-600 transition-colors disabled:opacity-40"
              >
                {creating ? "Creating..." : form.scheduled_for ? "Schedule" : "Save Draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={24} className="animate-spin text-copper" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Send size={40} className="mx-auto mb-3 text-gray-600" />
          <p>No posts yet. Create your first Google Business post.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => {
            const st = POST_STATUS[post.status] || POST_STATUS.draft;
            return (
              <div key={post.id} className="bg-dark-200 rounded-xl border border-dark-50 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.bg} ${st.color}`}>
                        {st.label}
                      </span>
                      <span className="text-[10px] text-gray-600 bg-dark-100 px-2 py-0.5 rounded-full">
                        {post.post_type}
                      </span>
                    </div>
                    {post.title && <h4 className="text-white text-sm font-medium mb-1">{post.title}</h4>}
                    <p className="text-gray-400 text-sm line-clamp-2">{post.content}</p>
                    <div className="text-xs text-gray-600 mt-2">
                      {post.scheduled_for ? (
                        <span><Clock size={10} className="inline mr-1" />Scheduled: {new Date(post.scheduled_for).toLocaleString()}</span>
                      ) : post.published_at ? (
                        <span><CheckCircle size={10} className="inline mr-1" />Published: {new Date(post.published_at).toLocaleString()}</span>
                      ) : (
                        <span>Created: {new Date(post.created_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    {(post.status === "draft" || post.status === "scheduled") && (
                      <button
                        onClick={() => handlePublish(post.id)}
                        className="p-1.5 rounded-lg text-green-400 hover:bg-green-900/20 transition-colors"
                        title="Publish now"
                      >
                        <Send size={14} />
                      </button>
                    )}
                    {post.status !== "published" && (
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-900/20 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Insights Tab ───
function InsightsTab({ siteId }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/websites/${siteId}/gbp/insights/`, { params: { period } });
      setInsights(res.data);
    } catch {
      setInsights(null);
    }
    setLoading(false);
  }, [siteId, period]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size={24} className="animate-spin text-copper" />
      </div>
    );
  }

  if (!insights || insights.error) {
    return (
      <div className="text-center py-16 text-gray-500">
        <BarChart3 size={40} className="mx-auto mb-3 text-gray-600" />
        <p>Connect your Google Business Profile to see insights.</p>
      </div>
    );
  }

  const views = insights.views || {};
  const actions = insights.actions || {};

  return (
    <div>
      {/* Period Selector */}
      <div className="flex gap-2 mb-6">
        {[7, 30, 90].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              period === p
                ? "bg-copper/10 text-copper border border-copper/30"
                : "bg-dark-200 text-gray-400 border border-dark-50 hover:text-white"
            }`}
          >
            {p} days
          </button>
        ))}
      </div>

      {/* Views */}
      <h3 className="text-white font-semibold mb-3">Profile Views</h3>
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: "Total Views", value: views.total || 0, icon: Eye, color: "text-copper" },
          { label: "Search Views", value: views.search || 0, icon: Globe, color: "text-blue-400" },
          { label: "Maps Views", value: views.maps || 0, icon: MapPin, color: "text-green-400" },
        ].map((m, i) => (
          <div key={i} className="bg-dark-200 rounded-xl border border-dark-50 p-5">
            <m.icon size={20} className={`${m.color} mb-3`} />
            <div className={`text-3xl font-bold ${m.color}`}>{m.value.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <h3 className="text-white font-semibold mb-3">Customer Actions</h3>
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Phone Calls", value: actions.calls || 0, icon: Phone, color: "text-green-400" },
          { label: "Website Clicks", value: actions.website_clicks || 0, icon: Globe, color: "text-blue-400" },
          { label: "Direction Requests", value: actions.directions || 0, icon: Navigation, color: "text-amber-400" },
        ].map((m, i) => (
          <div key={i} className="bg-dark-200 rounded-xl border border-dark-50 p-5">
            <m.icon size={20} className={`${m.color} mb-3`} />
            <div className={`text-3xl font-bold ${m.color}`}>{m.value.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Q&A Tab ───
function QATab({ siteId }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQ, setSelectedQ] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/websites/${siteId}/gbp/questions/`);
      setQuestions(res.data.results || res.data);
    } catch {
      // handled
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerateAI = async (q) => {
    setGenerating(true);
    try {
      const res = await api.post(`/websites/${siteId}/gbp/questions/${q.id}/ai-answer/`);
      setAnswerText(res.data.suggestion || "");
    } catch {
      // handled
    }
    setGenerating(false);
  };

  const handlePostAnswer = async () => {
    if (!answerText.trim() || !selectedQ) return;
    setPosting(true);
    try {
      await api.post(`/websites/${siteId}/gbp/questions/${selectedQ.id}/post-answer/`, {
        answer_text: answerText,
      });
      setSelectedQ(null);
      setAnswerText("");
      load();
    } catch {
      // handled
    }
    setPosting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size={24} className="animate-spin text-copper" />
      </div>
    );
  }

  return (
    <div>
      {questions.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <HelpCircle size={40} className="mx-auto mb-3 text-gray-600" />
          <p>No questions yet. Questions from your Google Business listing will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => (
            <div
              key={q.id}
              className="bg-dark-200 rounded-xl border border-dark-50 p-4 cursor-pointer hover:border-dark-50/80 transition-colors"
              onClick={() => {
                setSelectedQ(q);
                setAnswerText(q.ai_suggested_answer || q.answer_text || "");
              }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <HelpCircle size={14} className="text-copper" />
                    <span className="text-white text-sm font-medium">{q.asked_by}</span>
                    <span className="text-xs text-gray-600">{new Date(q.asked_date).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-300 text-sm">{q.question_text}</p>
                  {q.answered && q.answer_text && (
                    <div className="mt-2 pl-4 border-l-2 border-green-800">
                      <p className="text-gray-400 text-sm">{q.answer_text}</p>
                      <span className="text-[10px] text-green-400 mt-1 inline-block">
                        Answered {q.answered_date ? new Date(q.answered_date).toLocaleDateString() : ""}
                      </span>
                    </div>
                  )}
                </div>
                {!q.answered && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-900/20 text-amber-400 whitespace-nowrap">
                    Unanswered
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Answer Modal */}
      {selectedQ && !selectedQ.answered && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-300 rounded-xl border border-dark-50 w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-dark-50">
              <h3 className="text-white font-semibold">Answer Question</h3>
              <button onClick={() => setSelectedQ(null)} className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-gray-300 text-sm mb-4">{selectedQ.question_text}</p>
              <textarea
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                rows={5}
                placeholder="Write your answer..."
                className="w-full bg-dark-100 border border-dark-50 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper resize-none"
              />
              <div className="flex justify-between mt-3">
                <button
                  onClick={() => handleGenerateAI(selectedQ)}
                  disabled={generating}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-dark-200 text-copper border border-copper/30 hover:bg-copper/10 transition-colors"
                >
                  <Sparkles size={14} className={generating ? "animate-pulse" : ""} />
                  {generating ? "Generating..." : "AI Suggest"}
                </button>
                <button
                  onClick={handlePostAnswer}
                  disabled={posting || !answerText.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-copper text-dark hover:bg-copper-600 transition-colors disabled:opacity-40"
                >
                  <Send size={14} />
                  {posting ? "Posting..." : "Post Answer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───
export default function GoogleBusiness({ selectedSite }) {
  const [tab, setTab] = useState("reviews");
  const [gbp, setGbp] = useState(null);
  const [loading, setLoading] = useState(true);

  const siteId = selectedSite?.id;

  const loadGbp = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const res = await api.get(`/websites/${siteId}/gbp/status/`);
      setGbp(res.data);
    } catch {
      setGbp(null);
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { loadGbp(); }, [loadGbp]);

  if (!selectedSite) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Select a website to manage Google Business Profile.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Google Business Profile</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage reviews, posts, insights, and Q&A for your Google Business listing.
          </p>
        </div>
      </div>

      {/* Connection Card */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={24} className="animate-spin text-copper" />
        </div>
      ) : (
        <>
          <ConnectionCard siteId={siteId} gbp={gbp} onRefresh={loadGbp} />

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-dark-200 rounded-xl p-1 border border-dark-50 w-fit">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === key
                    ? "bg-copper/10 text-copper"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {tab === "reviews" && <ReviewsTab siteId={siteId} />}
          {tab === "posts" && <PostsTab siteId={siteId} selectedSite={selectedSite} />}
          {tab === "insights" && <InsightsTab siteId={siteId} />}
          {tab === "qa" && <QATab siteId={siteId} />}
        </>
      )}
    </div>
  );
}
